import {
  doc,
  setDoc,
  updateDoc,
  deleteField,
  arrayUnion,
  arrayRemove,
  serverTimestamp,
} from 'firebase/firestore'
import { db } from '@/lib/firebase'
import type { BinderConfig, KidneyProfile, UserProfile } from '@/types'

export async function updateUserProfile(uid: string, partial: Partial<UserProfile>): Promise<void> {
  // firestore.rules makes `id` and `createdAt` immutable on kpal_users updates — never send them.
  const { id: _id, createdAt: _c, ...rest } = partial as Partial<UserProfile> & { createdAt?: unknown }
  await setDoc(doc(db, 'kpal_users', uid), { ...rest, updatedAt: serverTimestamp() }, { merge: true })
}

/** Deep-merges into kidneyProfile (Firestore merge merges nested maps). */
export async function updateKidneyProfile(uid: string, partial: Partial<KidneyProfile>): Promise<void> {
  await setDoc(
    doc(db, 'kpal_users', uid),
    { kidneyProfile: partial, updatedAt: serverTimestamp() },
    { merge: true },
  )
}

/**
 * Precise dotted-path updates inside kidneyProfile. Unlike a whole-map replace,
 * concurrent edits to OTHER kidneyProfile fields are never clobbered. Pass a
 * value to set a field, or null to delete it.
 */
export async function updateKidneyProfileFields(
  uid: string,
  fields: Record<string, unknown | null>,
): Promise<void> {
  const updates: Record<string, unknown> = { updatedAt: serverTimestamp() }
  for (const [key, value] of Object.entries(fields)) {
    updates[`kidneyProfile.${key}`] = value === null ? deleteField() : value
  }
  await updateDoc(doc(db, 'kpal_users', uid), updates)
}

/**
 * Like updateKidneyProfileFields, but null is stored as a LITERAL null instead
 * of deleting the field. Needed for optional-nutrient targets, where explicit
 * null means "track without target" and a deleted (absent) field means "never
 * set" — the distinction that keeps the seeded 50 mg oxalate default from
 * coming back after a stone former clears it (mobile parity).
 */
export async function updateKidneyProfileFieldsKeepNull(
  uid: string,
  fields: Record<string, unknown | null>,
  deletePaths: string[] = [],
): Promise<void> {
  const updates: Record<string, unknown> = { updatedAt: serverTimestamp() }
  for (const [key, value] of Object.entries(fields)) {
    updates[`kidneyProfile.${key}`] = value
  }
  // Deletions ride the SAME updateDoc so a partial failure can never leave a
  // literal-null legacy value alongside a still-winning canonical target.
  for (const p of deletePaths) {
    updates[`kidneyProfile.${p}`] = deleteField()
  }
  await updateDoc(doc(db, 'kpal_users', uid), updates)
}

/** Race-free binder add/remove via array transforms on kidneyProfile.binders. */
export async function addBinderConfig(uid: string, binder: BinderConfig): Promise<void> {
  await updateDoc(doc(db, 'kpal_users', uid), {
    'kidneyProfile.binders': arrayUnion(binder),
    updatedAt: serverTimestamp(),
  })
}

export async function removeBinderConfig(uid: string, binder: BinderConfig): Promise<void> {
  await updateDoc(doc(db, 'kpal_users', uid), {
    'kidneyProfile.binders': arrayRemove(binder),
    updatedAt: serverTimestamp(),
  })
}

/**
 * Set/clear individual custom daily limits via dotted paths — mirrors mobile's
 * FieldValue.delete() pattern (settings.tsx handleSaveLimit / reset).
 * Pass a number to set, or null to clear back to the calculated limit.
 */
export async function setCustomLimits(
  uid: string,
  limits: Partial<Record<'dailySodiumLimit' | 'dailyPotassiumLimit' | 'dailyPhosphorusLimit' | 'dailyProteinLimit' | 'dailyFluidLimit', number | null>>,
): Promise<void> {
  const updates: Record<string, unknown> = { updatedAt: serverTimestamp() }
  for (const [field, value] of Object.entries(limits)) {
    if (value === undefined) continue
    updates[`kidneyProfile.${field}`] = value === null ? deleteField() : value
  }
  await updateDoc(doc(db, 'kpal_users', uid), updates)
}

/**
 * Record a completed Paddle checkout so the existing `transferSubscription`
 * Firestore trigger picks it up (keys off webFunnelData.paddleTransactionId),
 * POSTs it to RevenueCat, and flips isPremium.
 *
 * Deliberately `updateDoc`, NOT a merge-set: for a user with no kpal_users doc
 * a merge-set would create a stub, and mobile's createUserProfile transaction
 * then refuses to create the real profile (permanently broken account). With
 * updateDoc the write fails cleanly ('not-found') and the Paddle webhook still
 * activates the subscription server-side.
 */
export async function writePaddleTransaction(
  uid: string,
  transactionId: string,
  email?: string | null,
): Promise<void> {
  await updateDoc(doc(db, 'kpal_users', uid), {
    webFunnelData: {
      source: 'web-app',
      paddleTransactionId: transactionId,
      email: email ?? null,
      createdAt: new Date().toISOString(),
    },
    updatedAt: serverTimestamp(),
  })
}
